import { LifeEventType, Post, PostFilePayload } from "@/src/models/post.model";
import { pickFile, pickMedia } from "@/src/utils/picker-utils";
import { useEffect, useState, useCallback, useRef } from "react";
import { Platform } from "react-native";
import { useUploadFile } from "../use-upload-file";
import { SelectableItem } from "@/src/components/action-modals/item-selection/item-row";

export type VisibilityType = "public" | "group" | "specific" | "private";

// Matches @ at the start of text or immediately after whitespace.
// Does NOT match mid-word (e.g. a@b, email@domain).
export const INLINE_MENTION_TRIGGER = /(?:^|\s)@$/;

/**
 * Returns true when `text` should open the inline mention picker:
 * @ is the last character and is either at the start of the field or
 * preceded by whitespace. Email addresses and mid-word @ are excluded.
 * Exported for unit testing.
 */
export const shouldTriggerInlineMention = (text: string): boolean =>
  INLINE_MENTION_TRIGGER.test(text);

// Same rule as INLINE_MENTION_TRIGGER, but captures what precedes the '@' so the
// replacement can put it back. Kept separate because the trigger uses a
// non-capturing group and is matched against text the user is still typing.
const INLINE_MENTION_STRIP = /(^|\s)@$/;

/**
 * Removes the trailing '@' that opened the mention picker, keeping the character
 * that preceded it (start-of-field or the whitespace).
 *
 * Once the pill exists the '@' is no longer a mention-in-progress — leaving it
 * behind reads as an unfinished mention and re-arms the trigger on the next
 * keystroke. Text that does not end in a trigger is returned unchanged, so a
 * half-typed email ("user@") is never touched.
 *
 * Exported for unit testing.
 */
export const stripInlineMentionTrigger = (text: string): string =>
  text.replace(INLINE_MENTION_STRIP, (_match, lead: string) => lead);

/**
 * Append `newItems` to `existing`, deduplicating by `itemId`.
 * Exported for unit testing.
 */
export const appendNewMentions = <T extends { itemId: string }>(
  existing: T[],
  newItems: T[]
): T[] => {
  const existingIds = new Set(existing.map((u) => u.itemId));
  return [...existing, ...newItems.filter((u) => !existingIds.has(u.itemId))];
};

export const usePostCreationModal = (
  post: Post | undefined,
  visible: boolean,
  onSubmit: (
    content: string,
    hashtags: string[],
    mentionedUsers: SelectableItem[],
    visibility: VisibilityType,
    attachmentUrls: string[],
    visibleToGroupIds: string[],
    visibleToUserIds: string[],
    groupId?: string,
    lifeEventType?: LifeEventType,
    lifeEventDate?: string,
  ) => void,
  onClose: () => void,
  isPostingFromParent: boolean = false,
  insideGroup?: boolean,
  groupId?: string
) => {
  // Form state
  const [content, setContent] = useState("");
  const [hashtags, setHashtags] = useState<string[]>([]);
  const [selectedMedia, setSelectedMedia] = useState<PostFilePayload[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<PostFilePayload[]>([]);
  const [showVisibilityModal, setShowVisibilityModal] = useState(false);
  const [showMentionModal, setShowMentionModal] = useState(false);
  const [showLifeUpdatePicker, setShowLifeUpdatePicker] = useState(false);
  const [lifeEventType, setLifeEventType] = useState<LifeEventType | undefined>(undefined);
  const [lifeEventDate, setLifeEventDate] = useState<string | undefined>(undefined);
  const [visibility, setVisibility] = useState<VisibilityType>("public");
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [mentionedUsers, setMentionedUsers] = useState<SelectableItem[]>([]);

  const toggleGroup = useCallback((item: SelectableItem) => {
    setSelectedGroupIds(prev =>
      prev.includes(item.itemId) ? prev.filter(id => id !== item.itemId) : [...prev, item.itemId]
    );
  }, []);

  const toggleUser = useCallback((item: SelectableItem) => {
    setSelectedUserIds(prev =>
      prev.includes(item.itemId) ? prev.filter(id => id !== item.itemId) : [...prev, item.itemId]
    );
  }, []);

  const [isUploading, setIsUploading] = useState(false);

  const { mutateAsync: uploadFile } = useUploadFile();

  // Combined loading state
  const isLoading = isPostingFromParent || isUploading;

  // Holds the exact content string that last opened the inline mention picker.
  // Prevents the picker from immediately reopening when the user cancels and the
  // text still ends with '@'.  Cleared when '@' is removed from the content.
  const suppressedMentionContent = useRef<string | null>(null);

  useEffect(() => {
    if (post?.visibility) setVisibility(post.visibility as VisibilityType);
  }, [post]);

  useEffect(() => {
    switch (visibility) {
      case "group":
        setSelectedUserIds([]);
        break;
      case "specific":
        setSelectedGroupIds([]);
        break;
      default:
        setSelectedGroupIds([]);
        setSelectedUserIds([]);
    }
  }, [visibility]);

  // Populate form when modal opens or post changes
  useEffect(() => {
    if (visible) {
      const isGroupPost = Boolean(insideGroup && groupId);
      setContent(post?.content ?? "");
      setHashtags(post?.hashtags ?? []);
      setMentionedUsers(post?.mentionedUsers ?? []);
      setVisibility(
        (post?.visibility as VisibilityType) ??
          (isGroupPost ? "group" : "public"),
      );
      setSelectedGroupIds(
        post?.visibleToGroupIds ?? (isGroupPost ? [groupId!] : []),
      );
      setSelectedUserIds(post?.visibleToUserIds ?? []);
      // Without these, editing a life-update post opened the form with the
      // category and date blank, so saving silently stripped them.
      setLifeEventType(post?.lifeEventType);
      setLifeEventDate(post?.lifeEventDate);

      // Split attachments into media and files
      const mediaUrls = post?.attachmentUrls?.filter((url) =>
        /\.(jpg|jpeg|png|gif|webp|mp4|mov|avi)$/i.test(url)
      ) ?? [];

      const fileUrls = post?.attachmentUrls?.filter((url) =>
        !/\.(jpg|jpeg|png|gif|webp|mp4|mov|avi)$/i.test(url)
      ) ?? [];

      setSelectedMedia(
        mediaUrls.map((url) => ({
          uri: url,
          fileName: url.split("/").pop() ?? "",
          type: /\.(mp4|mov|avi)$/i.test(url) ? "video/mp4" : "image/jpeg",
          file_size: 0,
        }))
      );

      setSelectedFiles(
        fileUrls.map((url) => ({
          uri: url,
          fileName: url.split("/").pop() ?? "",
          type: "application/octet-stream",
          file_size: 0,
        }))
      );
    }
  }, [visible, post]);

  const handleSubmit = async () => {
    if (isLoading) return;
    if (!content.trim() && !selectedMedia.length && !selectedFiles.length) return;

    setIsUploading(true);

    try {
      const allFiles = [...selectedMedia, ...selectedFiles];

      const attachmentUrls = await Promise.all(
        allFiles.map((file) =>
          uploadFile({
            uri: file.uri,
            fileName: file.fileName,
            mimeType: file.type,
          })
        )
      );

      onSubmit(
        content.trim(),
        hashtags,
        mentionedUsers,
        visibility,
        attachmentUrls as string[],
        selectedGroupIds,
        selectedUserIds,
        insideGroup && groupId ? groupId : undefined,
        lifeEventType,
        lifeEventDate,
      );

      resetForm();
    } catch (error) {
      console.error("Error uploading files:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const resetForm = () => {
    setContent("");
    setHashtags([]);
    setSelectedMedia([]);
    setSelectedFiles([]);
    setMentionedUsers([]);
    setSelectedGroupIds([]);
    setSelectedUserIds([]);
    setLifeEventType(undefined);
    setLifeEventDate(undefined);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  /**
   * Content change handler that also detects the inline @ mention trigger.
   *
   * Trigger rule (CR-B2-03):
   *   Open the picker when @ is typed at the start of the field or immediately
   *   after whitespace — this excludes email addresses (user@domain) and
   *   mid-word handles (a@b).
   *
   * The @ stays in the text while the picker is open so the user can see what
   * they typed; `handleMentionConfirm` strips it once a pill is added.
   */
  const handleContentChange = useCallback(
    (text: string) => {
      setContent(text);

      // If the @ that triggered suppression was removed, allow the next @
      if (suppressedMentionContent.current !== null && !text.endsWith("@")) {
        suppressedMentionContent.current = null;
      }

      // Inline @ trigger: fire on @ at start or after whitespace
      if (
        INLINE_MENTION_TRIGGER.test(text) &&
        text !== suppressedMentionContent.current
      ) {
        suppressedMentionContent.current = text;
        setShowMentionModal(true);
      }
    },
    [] // no deps — refs are stable, setShowMentionModal is stable
  );

  /**
   * Confirm handler for the mention picker.
   *
   * Always appends: the inline @ is now the only way to open the picker (the
   * toolbar mention button was removed), and it opens with nothing preselected,
   * so replacing the list would silently drop the pills already added. Pills are
   * removed individually via MentionedUserPill.
   *
   * Also clears the '@' that opened the picker — see stripInlineMentionTrigger.
   */
  const handleMentionConfirm = useCallback((items: SelectableItem[]) => {
    setMentionedUsers((prev) => appendNewMentions(prev, items));
    setContent(stripInlineMentionTrigger);
    suppressedMentionContent.current = null;
  }, []);

  /**
   * Cancel handler: suppresses immediate retrigger for the current content value.
   * Once the user removes the @ or changes the text significantly, the trigger re-arms.
   */
  const handleMentionCancel = useCallback(() => {
    // Snapshot current content to prevent the same text from reopening the picker
    // on the very next change event (AC-B2-03-05).
    suppressedMentionContent.current =
      suppressedMentionContent.current ?? null;
    setShowMentionModal(false);
  }, []);

  const handlePickImage = async () => {
    // cropImages: post attachments get the same crop step as avatar/cover.
    const images = await pickMedia(false, "all", 10, true);
    if (images && Array.isArray(images)) {
      const mapped = images.map((img) => ({
        uri: img.uri,
        fileName: img.fileName as string,
        type: img.mimeType ?? "image/jpeg",
        file: Platform.OS === "web" ? (img as any).file : undefined,
        file_size: img.fileSize ?? 0,
      }));
      setSelectedMedia((prev) => [...prev, ...mapped]);
    }
  };

  const handleCameraPress = async () => {
    try {
      const image = await pickMedia(true, "all", 10, true);
      if (image) {
        const assets = Array.isArray(image) ? image : [image];
        const mapped = assets.map((img) => ({
          uri: img.uri,
          fileName: img.fileName ?? `image_${Date.now()}.jpg`,
          type: img.mimeType ?? "image/jpeg",
          file: Platform.OS === "web" ? (img as any).file : undefined,
          file_size: img.fileSize ?? 0,
        }));
        setSelectedMedia((prev) => [...prev, ...mapped]);
      }
    } catch (error) {
      console.error("Error taking photo:", error);
    }
  };

  const handlePickFile = async () => {
    try {
      const result = await pickFile(true, 10);
      if (result && !result.canceled && result.assets) {
        const mapped = result.assets.map((asset) => ({
          uri: asset.uri,
          fileName: asset.name,
          type: asset.mimeType ?? "application/octet-stream",
          file: Platform.OS === "web" ? (asset as any).file : undefined,
          file_size: asset.size ?? 0,
        }));
        setSelectedFiles((prev) => [...prev, ...mapped]);
      }
    } catch (error) {
      console.error("Error picking file:", error);
    }
  };

  const handleRemoveMedia = (index: number) => {
    setSelectedMedia((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveFile = (uri: string) => {
    setSelectedFiles((prev) => prev.filter((f) => f.uri !== uri));
  };

  const hasContent =
    content.trim().length > 0 ||
    selectedMedia.length > 0 ||
    selectedFiles.length > 0;

  return {
    content,
    setContent,
    hashtags,
    setHashtags,
    visibility,
    setVisibility,
    selectedMedia,
    selectedFiles,
    mentionedUsers,
    setMentionedUsers,
    showVisibilityModal,
    setShowVisibilityModal,
    showMentionModal,
    setShowMentionModal,
    showLifeUpdatePicker,
    setShowLifeUpdatePicker,
    lifeEventType,
    setLifeEventType,
    lifeEventDate,
    setLifeEventDate,
    selectedGroupIds,
    selectedUserIds,
    setSelectedGroupIds,
    setSelectedUserIds,
    toggleGroup,
    toggleUser,
    hasContent,
    isLoading,
    handleSubmit,
    handleClose,
    handlePickImage,
    handleCameraPress,
    handlePickFile,
    handleMentionConfirm,
    handleMentionCancel,
    handleContentChange,
    handleRemoveMedia,
    handleRemoveFile,
  };
};
